<!-- IA Dashboard view -->
<script>
import Layout from "../../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../../app.config";
import iaNumbers from "../../widgets/iaNumbers.vue";
import SkeletonStats from "@/components/widgets/SkeletonStats.vue";
import AIAgentSettings from "./AIAgentSettings.vue";
import axios from "axios";
import jwt_decode from "jwt-decode";
import Swal from "sweetalert2";

export default {
  page: {
    title: "AI Dashboard",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "AI Dashboard",
      items: [
        {
          text: "Dashboards",
          href: "/",
        },
        {
          text: "AI Dashboard",
          active: true,
        },
      ],
      loading: false,
      stats: {
        totalSent: 0,
        totalReceived: 0,
        totalCandidates: 0,
        totalReplied: 0,
        responseRate: 0.0,
        blacklistCount: 0
      },
      // Conversations listing state
      conversations: [],
      conversationsLoading: false,
      search: "",
      sourceFilter: "all",
      page: 1,
      limit: 10,
      totalCount: 0,
      pageCount: 1,
      // Chat modal state
      selectedPhone: "",
      selectedName: "",
      chatModalOpen: false,
      chatMessages: [],
      chatLoading: false,
      // Voice Calls state
      voiceCalls: [],
      voiceCallsLoading: false,
      voiceSearch: "",
      voicePage: 1,
      voiceLimit: 10,
      voiceTotalCount: 0,
      voicePageCount: 1,
      // Voice Modal state
      selectedVoiceCall: null,
      voiceModalOpen: false,
      // Tools state
      tools: [],
      toolsLoading: false,
      toolModalOpen: false,
      editingTool: null,
      toolForm: { key: "", name: "", description: "", parameters_schema: "{}", is_active: true },
      toolFormError: "",
      savingTool: false,
      // Tabs and admin view
      activeTab: "conversations",
      isAdmin: false
    };
  },
  components: {
    Layout,
    PageHeader,
    iaNumbers,
    SkeletonStats,
    AIAgentSettings
  },
  methods: {
    async loadConversations() {
      this.conversationsLoading = true;
      const data = {
        token: localStorage.getItem('jwt'),
        page: this.page,
        limit: this.limit,
        search: this.search,
        source: this.sourceFilter
      };

      await axios
        .post('/api/ia/conversationsList', data)
        .then(response => {
          if (response.data && response.data.success) {
            this.conversations = response.data.conversations;
            this.totalCount = response.data.totalCount;
            this.pageCount = response.data.pageCount;
          }
        })
        .catch(error => {
          console.error("Error loading IA Conversations:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          }
        })
        .finally(() => {
          this.conversationsLoading = false;
        });
    },
    changePage(p) {
      if (p >= 1 && p <= this.pageCount) {
        this.page = p;
        this.loadConversations();
      }
    },
    filterChange() {
      this.page = 1;
      this.loadConversations();
    },
    async viewChat(conv) {
      this.selectedPhone = conv.phone;
      this.selectedName = conv.name;
      this.chatModalOpen = true;
      this.chatLoading = true;
      this.chatMessages = [];

      const token = localStorage.getItem('jwt');
      await axios
        .get('/api/ia/conversation', {
          params: { fromNumber: conv.phone, token: token },
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => {
          if (response.data && response.data.success) {
            const rawMessages = response.data.messages || [];
            const cleanPhone = conv.phone.replace(/\D/g, '').slice(-10);
            this.chatMessages = rawMessages.map(msg => {
              const fromClean = (msg.fromNumber || '').replace(/\D/g, '').slice(-10);
              return { ...msg, is_candidate: fromClean === cleanPhone };
            });
          }
        })
        .catch(error => {
          console.error("Error loading chat history:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          } else {
            Swal.fire("Error", "Could not load chat history.", "error");
          }
        })
        .finally(() => {
          this.chatLoading = false;
        });
    },
    closeChatModal() {
      this.chatModalOpen = false;
      this.chatMessages = [];
    },
    formatChatDate(d) {
      if (!d) return "";
      return d.replace('T', ' ').substring(0, 16);
    },
    async loadVoiceCalls() {
      this.voiceCallsLoading = true;
      const data = {
        token: localStorage.getItem('jwt'),
        page: this.voicePage,
        limit: this.voiceLimit,
        search: this.voiceSearch
      };
      await axios.post('/api/ia/calls/list', data)
        .then(response => {
          if (response.data && response.data.success) {
            this.voiceCalls = response.data.calls;
            this.voiceTotalCount = response.data.totalCount;
            this.voicePageCount = response.data.pageCount;
          }
        })
        .catch(error => {
          console.error("Error loading Voice Calls:", error);
        })
        .finally(() => {
          this.voiceCallsLoading = false;
        });
    },
    changeVoicePage(p) {
      if (p >= 1 && p <= this.voicePageCount) {
        this.voicePage = p;
        this.loadVoiceCalls();
      }
    },
    filterVoiceChange() {
      this.voicePage = 1;
      this.loadVoiceCalls();
    },
    viewVoiceCall(call) {
      this.selectedVoiceCall = call;
      this.voiceModalOpen = true;
    },
    closeVoiceModal() {
      this.voiceModalOpen = false;
      this.selectedVoiceCall = null;
    },
    async loadTools() {
      this.toolsLoading = true;
      await axios.post('/api/ia/tools', { token: localStorage.getItem('jwt') })
        .then(response => {
          this.tools = response.data || [];
        })
        .catch(error => {
          console.error("Error loading tools:", error);
          if (error.response && (error.response.status === 401 || (error.response.data && error.response.data.detail === 'Your session is expired!'))) {
            this.$router.push('logout');
          } else {
            Swal.fire("Error", "Could not load tools.", "error");
          }
        })
        .finally(() => {
          this.toolsLoading = false;
        });
    },
    openCreateTool() {
      this.editingTool = null;
      this.toolForm = { key: "", name: "", description: "", parameters_schema: "{\n  \"type\": \"object\",\n  \"properties\": {}\n}", is_active: true };
      this.toolFormError = "";
      this.toolModalOpen = true;
    },
    openEditTool(tool) {
      this.editingTool = tool;
      this.toolForm = {
        key: tool.key,
        name: tool.name,
        description: tool.description,
        parameters_schema: JSON.stringify(tool.parameters_schema || {}, null, 2),
        is_active: tool.is_active
      };
      this.toolFormError = "";
      this.toolModalOpen = true;
    },
    closeToolModal() {
      this.toolModalOpen = false;
      this.editingTool = null;
    },
    async saveTool() {
      this.toolFormError = "";
      let parsedSchema;
      try {
        parsedSchema = JSON.parse(this.toolForm.parameters_schema || "{}");
      } catch (e) {
        this.toolFormError = "Parameters Schema must be valid JSON.";
        return;
      }

      if (!this.toolForm.name.trim() || !this.toolForm.description.trim()) {
        this.toolFormError = "Name and Description are required.";
        return;
      }

      const payload = {
        name: this.toolForm.name.trim(),
        description: this.toolForm.description.trim(),
        parameters_schema: parsedSchema,
        is_active: this.toolForm.is_active
      };

      this.savingTool = true;
      const token = localStorage.getItem('jwt');

      if (this.editingTool) {
        await axios.put(`/api/ia/tools/${this.editingTool.id}`, { token, tool: payload })
          .then(() => {
            Swal.fire({ icon: "success", title: "Tool updated", timer: 1500, showConfirmButton: false });
            this.closeToolModal();
            this.loadTools();
          })
          .catch(error => {
            this.toolFormError = "Failed to update tool.";
            console.error(error);
          })
          .finally(() => { this.savingTool = false; });
      } else {
        payload.key = this.toolForm.key.trim();
        if (!payload.key) {
          this.toolFormError = "Key is required for a new tool (must match a backend implementation).";
          this.savingTool = false;
          return;
        }
        await axios.put('/api/ia/tools', { token, tool: payload })
          .then(() => {
            Swal.fire({ icon: "success", title: "Tool created", timer: 1500, showConfirmButton: false });
            this.closeToolModal();
            this.loadTools();
          })
          .catch(error => {
            this.toolFormError = (error.response && error.response.data && error.response.data.key)
              ? "That key already exists."
              : "Failed to create tool.";
            console.error(error);
          })
          .finally(() => { this.savingTool = false; });
      }
    },
    async toggleToolActive(tool) {
      const token = localStorage.getItem('jwt');
      const newValue = !tool.is_active;
      await axios.put(`/api/ia/tools/${tool.id}`, { token, tool: { is_active: newValue } })
        .then(() => {
          tool.is_active = newValue;
        })
        .catch(error => {
          console.error("Error toggling tool status:", error);
          Swal.fire("Error", "Could not update tool status.", "error");
        });
    },
    async deleteTool(tool) {
      const result = await Swal.fire({
        title: `Delete "${tool.name}"?`,
        text: tool.agent_count > 0 ? `This tool is assigned to ${tool.agent_count} agent(s). It will be unassigned from all of them.` : "This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33"
      });
      if (!result.isConfirmed) return;

      const token = localStorage.getItem('jwt');
      await axios.delete(`/api/ia/tools/${tool.id}`, { data: { token } })
        .then(() => {
          this.tools = this.tools.filter(t => t.id !== tool.id);
          Swal.fire({ icon: "success", title: "Tool deleted", timer: 1500, showConfirmButton: false });
        })
        .catch(error => {
          console.error("Error deleting tool:", error);
          Swal.fire("Error", "Could not delete tool.", "error");
        });
    }
  },
  async mounted() {
    const token = localStorage.getItem('jwt');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        this.isAdmin = !!(decoded.userGroup && (decoded.userGroup.includes('SUPER_ADMIN_GROUP') || decoded.userGroup.includes('ADMIN_GROUP')));
      } catch (err) {
        console.error("Error decoding token in IA Dashboard:", err);
      }
    }

    this.loading = true;
    const data = {
      token: token
    };

    await axios
      .post('/api/ia/dashboardNumbers', data)
      .then(response => {
        if (response.data && response.data.success) {
          this.stats = response.data;
        }
      })
      .catch(error => {
        console.error("Error loading IA Dashboard metrics:", error);
        if (error.response && error.response.data && error.response.data.detail === 'Your session is expired!') {
          this.$router.push('logout');
        }
      })
      .finally(() => {
        this.loading = false;
      });

    // Also load recent conversations list
    this.loadConversations();
    this.loadVoiceCalls();
    if (this.isAdmin) {
      this.loadTools();
    }
  },
  computed: {
    parsedTranscript() {
      const raw = this.selectedVoiceCall && this.selectedVoiceCall.transcript;
      if (!raw) return [];
      return raw
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => {
          const idx = line.indexOf(': ');
          if (idx === -1) return { role: 'agent', label: '', text: line };
          const label = line.substring(0, idx).trim();
          const text = line.substring(idx + 2).trim();
          const isUser = /cliente|user|client/i.test(label);
          return { role: isUser ? 'user' : 'agent', label, text };
        });
    },
    displayedPages() {
      const range = [];
      const maxVisible = 5;
      let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
      let end = Math.min(this.pageCount, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Navigation Tabs -->
    <div class="row mb-4" v-if="isAdmin">
      <div class="col-12">
        <div class="card mb-0">
          <div class="card-body p-0">
            <ul class="nav nav-tabs nav-tabs-custom nav-success border-bottom-0" role="tablist">
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'conversations' }"
                  @click="activeTab = 'conversations'"
                  href="javascript:void(0);"
                  role="tab"
                >
                  <i class="ri-message-3-line align-middle me-1"></i> Conversations
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'voice' }"
                  @click="activeTab = 'voice'"
                  href="javascript:void(0);"
                  role="tab"
                >
                  <i class="ri-phone-line align-middle me-1"></i> Voice Calls
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'settings' }"
                  @click="activeTab = 'settings'"
                  href="javascript:void(0);"
                  role="tab"
                >
                  <i class="ri-settings-4-line align-middle me-1"></i> Emma Configuration
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'tools' }"
                  @click="activeTab = 'tools'; loadTools();"
                  href="javascript:void(0);"
                  role="tab"
                >
                  <i class="ri-tools-line align-middle me-1"></i> Tools
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversations Tab Content -->
    <div v-if="activeTab === 'conversations'">
      <div class="row">
        <div class="col-xl-12">
          <SkeletonStats v-if="loading" :count="6" />
          <ia-numbers v-else :stats="stats" class="fade-in" />
        </div>
      </div>

      <!-- Conversations Listing -->
    <div class="row mt-4">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-header align-items-center d-flex">
            <h4 class="card-title mb-0 flex-grow-1">Conversations List</h4>
          </div>
          <div class="card-body">
            <!-- Filters & Search -->
            <div class="row g-3 mb-4">
              <div class="col-md-4">
                <div class="search-box">
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search by name or phone..."
                    v-model="search"
                    @input="filterChange"
                  />
                </div>
              </div>
              <div class="col-md-3">
                <select class="form-select" v-model="sourceFilter" @change="filterChange">
                  <option value="all">All Sources</option>
                  <option value="Campaign/Chatbot">Campaign/Chatbot</option>
                  <option value="Indeed Chatbot">Indeed Chatbot</option>
                  <option value="SMS Follow-up">SMS Follow-up</option>
                  <option value="Prospecting IA">Prospecting IA</option>
                </select>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive table-card">
              <table class="table align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col">Candidate Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Source</th>
                    <th scope="col">Last Message</th>
                    <th scope="col">Date</th>
                    <th scope="col" style="width: 150px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="conversationsLoading">
                    <td colspan="6" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="conversations.length === 0">
                    <td colspan="6" class="text-center py-4 text-muted">
                      No conversations found.
                    </td>
                  </tr>
                  <tr v-else v-for="conv in conversations" :key="conv.phone">
                    <td class="fw-semibold">{{ conv.name }}</td>
                    <td>{{ conv.phone }}</td>
                    <td>
                      <span
                        class="badge rounded-pill"
                        :class="{
                          'bg-soft-primary text-primary': conv.source === 'Campaign/Chatbot',
                          'bg-soft-success text-success': conv.source === 'Indeed Chatbot',
                          'bg-soft-warning text-warning': conv.source === 'SMS Follow-up'
                        }"
                      >
                        {{ conv.source }}
                      </span>
                    </td>
                    <td class="text-truncate" style="max-width: 250px;">
                      <span class="text-muted" v-if="conv.direction === 'outbound'">→ </span>
                      <span class="text-info" v-else>← </span>
                      {{ conv.message }}
                    </td>
                    <td>{{ conv.date }}</td>
                    <td>
                      <button
                        class="btn btn-sm btn-soft-primary"
                        @click="viewChat(conv)"
                      >
                        <i class="ri-eye-line align-bottom me-1"></i> View Chat
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-content-end mt-3" v-if="pageCount > 1">
              <nav aria-label="Page navigation">
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: page === 1 }">
                    <button class="page-link" @click="changePage(page - 1)">Previous</button>
                  </li>

                  <!-- First Page Link -->
                  <li class="page-item" :class="{ active: page === 1 }" v-if="displayedPages[0] > 1">
                    <button class="page-link" @click="changePage(1)">1</button>
                  </li>
                  <li class="page-item disabled" v-if="displayedPages[0] > 2">
                    <span class="page-link">...</span>
                  </li>

                  <!-- Page Number Range -->
                  <li
                    class="page-item"
                    v-for="p in displayedPages"
                    :key="p"
                    :class="{ active: p === page }"
                  >
                    <button class="page-link" @click="changePage(p)">{{ p }}</button>
                  </li>

                  <!-- Last Page Link -->
                  <li class="page-item disabled" v-if="displayedPages[displayedPages.length - 1] < pageCount - 1">
                    <span class="page-link">...</span>
                  </li>
                  <li class="page-item" :class="{ active: page === pageCount }" v-if="displayedPages[displayedPages.length - 1] < pageCount">
                    <button class="page-link" @click="changePage(pageCount)">{{ pageCount }}</button>
                  </li>

                  <li class="page-item" :class="{ disabled: page === pageCount }">
                    <button class="page-link" @click="changePage(page + 1)">Next</button>
                  </li>
                </ul>
              </nav>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Chat Modal -->
    <div
      v-if="chatModalOpen"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0, 0, 0, 0.5); z-index: 1050;"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg bg-body-tertiary">
          <div class="modal-header border-bottom py-3 px-4">
            <h5 class="modal-title fw-bold">Chat History: {{ selectedName }}</h5>
            <button type="button" class="btn-close" @click="closeChatModal"></button>
          </div>
          <div class="modal-body p-4" style="max-height: 500px; overflow-y: auto;">
            <!-- Loading -->
            <div v-if="chatLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <!-- Chat history thread -->
            <div v-else class="chat-thread" style="padding: 4px 2px;">
              <div
                v-for="(msg, idx) in chatMessages"
                :key="idx"
                class="d-flex mb-2"
                :class="msg.is_candidate ? 'justify-content-start' : 'justify-content-end'"
              >
                <!-- Incoming (candidate / "Agent") -->
                <div
                  v-if="msg.is_candidate"
                  style="
                    max-width: 72%;
                    background: #f1f3f4;
                    color: #212529;
                    border-radius: 16px 16px 16px 4px;
                    padding: 8px 14px;
                    font-size: 0.85rem;
                  "
                >
                  <div class="fw-semibold mb-1" style="font-size:0.7rem; color: #5f6368;">Agent</div>
                  <div>{{ msg.message }}</div>
                  <div class="mt-1" style="font-size:0.65rem; text-align:right; color: #7f8388;">{{ formatChatDate(msg.createdOn) }}</div>
                </div>

                <!-- Outgoing (agency / IA) -->
                <div
                  v-else
                  style="
                    max-width: 72%;
                    background: linear-gradient(135deg, #405189, #0ab39c);
                    color: #fff;
                    border-radius: 16px 16px 4px 16px;
                    padding: 8px 14px;
                    font-size: 0.85rem;
                  "
                >
                  <div class="fw-semibold mb-1" style="font-size:0.7rem; opacity:.8;">Agency (IA)</div>
                  <div>{{ msg.message }}</div>
                  <div class="mt-1" style="font-size:0.68rem; text-align:right; opacity:.75;">{{ formatChatDate(msg.createdOn) }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top py-2 px-4">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeChatModal">Close</button>
          </div>
        </div>
      </div>
    </div>

    </div> <!-- Close Conversations Tab Content -->

    <!-- Voice Calls Tab Content -->
    <div v-if="activeTab === 'voice'">
      <div class="row mt-4">
        <div class="col-xl-12">
          <div class="card">
            <div class="card-header align-items-center d-flex">
              <h4 class="card-title mb-0 flex-grow-1">Voice Calls History</h4>
            </div>
            <div class="card-body">
              <div class="row g-3 mb-4">
                <div class="col-md-4">
                  <div class="search-box">
                    <input type="text" class="form-control" placeholder="Search by phone..." v-model="voiceSearch" @input="filterVoiceChange" />
                  </div>
                </div>
              </div>
              <div class="table-responsive table-card">
                <table class="table align-middle table-nowrap mb-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">Phone</th>
                      <th scope="col">Direction</th>
                      <th scope="col">Duration (s)</th>
                      <th scope="col">Status</th>
                      <th scope="col">Date</th>
                      <th scope="col" style="width: 150px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="voiceCallsLoading">
                      <td colspan="6" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
                      </td>
                    </tr>
                    <tr v-else-if="voiceCalls.length === 0">
                      <td colspan="6" class="text-center py-4 text-muted">No voice calls found.</td>
                    </tr>
                    <tr v-else v-for="call in voiceCalls" :key="call.call_id">
                      <td class="fw-semibold">{{ call.phone_number }}</td>
                      <td>
                        <span class="badge rounded-pill" :class="call.direction === 'inbound' ? 'bg-soft-info text-info' : 'bg-soft-primary text-primary'">
                          {{ call.direction }}
                        </span>
                      </td>
                      <td>{{ call.duration_seconds }} s</td>
                      <td>{{ call.status }}</td>
                      <td>{{ call.date }}</td>
                      <td>
                        <button class="btn btn-sm btn-soft-primary" @click="viewVoiceCall(call)">
                          <i class="ri-eye-line align-bottom me-1"></i> Details
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Voice Call Modal -->
      <div v-if="voiceModalOpen && selectedVoiceCall" class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5); z-index: 1050;">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 shadow-lg">
            <div class="modal-header border-bottom py-3 px-4">
              <h5 class="modal-title fw-bold">Call Details: {{ selectedVoiceCall.phone_number }}</h5>
              <button type="button" class="btn-close" @click="closeVoiceModal"></button>
            </div>
            <div class="modal-body p-4">
              <!-- Call metadata -->
              <div class="row gy-3 mb-3">
                <div class="col-6 col-md-3">
                  <div class="text-muted small">Call ID</div>
                  <div class="fw-semibold text-truncate">{{ selectedVoiceCall.call_id }}</div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted small">Agent Name</div>
                  <div class="fw-semibold">{{ selectedVoiceCall.agent_name || 'Emma' }}</div>
                </div>
                <div class="col-6 col-md-3">
                  <div class="text-muted small">Date</div>
                  <div class="fw-semibold">{{ selectedVoiceCall.date }}</div>
                </div>
                <div class="col-4 col-md-2">
                  <div class="text-muted small">Duration</div>
                  <div class="fw-semibold">{{ selectedVoiceCall.duration_seconds }}s</div>
                </div>
                <div class="col-4 col-md-1">
                  <div class="text-muted small">Status</div>
                  <span class="badge rounded-pill bg-soft-success text-success">{{ selectedVoiceCall.status }}</span>
                </div>
              </div>

              <!-- Recording -->
              <div class="mb-3" v-if="selectedVoiceCall.recording_url">
                <label class="form-label fw-semibold">Recording</label><br/>
                <audio controls style="width: 100%;">
                  <source :src="selectedVoiceCall.recording_url" type="audio/mpeg">
                  Your browser does not support the audio element.
                </audio>
              </div>

              <!-- Summary -->
              <div class="mb-3" v-if="selectedVoiceCall.call_summary">
                <label class="form-label fw-semibold">Summary</label>
                <div class="p-3 bg-light rounded">{{ selectedVoiceCall.call_summary }}</div>
              </div>

              <!-- Transcript as separate dialogue turns -->
              <div>
                <label class="form-label fw-semibold">Transcript</label>
                <div class="chat-thread p-2" style="max-height: 320px; overflow-y: auto;">
                  <div v-if="parsedTranscript.length === 0" class="text-muted">No transcript available.</div>
                  <div
                    v-for="(turn, idx) in parsedTranscript"
                    :key="idx"
                    class="d-flex mb-2"
                    :class="turn.role === 'user' ? 'justify-content-start' : 'justify-content-end'"
                  >
                    <!-- Client turn -->
                    <div
                      v-if="turn.role === 'user'"
                      style="
                        max-width: 78%;
                        background: #f1f3f4;
                        color: #212529;
                        border-radius: 16px 16px 16px 4px;
                        padding: 8px 14px;
                        font-size: 0.85rem;
                      "
                    >
                      <div class="fw-semibold mb-1" style="font-size:0.7rem; color: #5f6368;">{{ turn.label || 'Cliente' }}</div>
                      <div>{{ turn.text }}</div>
                    </div>

                    <!-- Agent turn -->
                    <div
                      v-else
                      style="
                        max-width: 78%;
                        background: linear-gradient(135deg, #405189, #0ab39c);
                        color: #fff;
                        border-radius: 16px 16px 4px 16px;
                        padding: 8px 14px;
                        font-size: 0.85rem;
                      "
                    >
                      <div class="fw-semibold mb-1" style="font-size:0.7rem; opacity:.8;">{{ turn.label || 'Agent' }}</div>
                      <div>{{ turn.text }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer border-top py-2 px-4">
              <button type="button" class="btn btn-secondary btn-sm" @click="closeVoiceModal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Agent Settings Tab Content -->
    <div v-if="activeTab === 'settings' && isAdmin" class="mt-4">
      <AIAgentSettings />
    </div>

    <!-- Tools Tab Content -->
    <div v-if="activeTab === 'tools' && isAdmin">
      <div class="row mt-4">
        <div class="col-xl-12">
          <div class="card">
            <div class="card-header align-items-center d-flex">
              <h4 class="card-title mb-0 flex-grow-1">Agent Tools</h4>
              <button class="btn btn-sm btn-primary" @click="openCreateTool">
                <i class="ri-add-line align-bottom me-1"></i> New Tool
              </button>
            </div>
            <div class="card-body">
              <p class="text-muted fs-13 mb-3">
                Capabilities that can be assigned to an agent (in its Configuration panel) so it can use them during conversations, by voice or SMS.
              </p>
              <div class="table-responsive table-card">
                <table class="table align-middle table-nowrap mb-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Key</th>
                      <th scope="col">Description</th>
                      <th scope="col">Agents Using It</th>
                      <th scope="col">Active</th>
                      <th scope="col" style="width: 120px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="toolsLoading">
                      <td colspan="6" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
                      </td>
                    </tr>
                    <tr v-else-if="tools.length === 0">
                      <td colspan="6" class="text-center py-4 text-muted">No tools created yet.</td>
                    </tr>
                    <tr v-else v-for="tool in tools" :key="tool.id">
                      <td class="fw-semibold">{{ tool.name }}</td>
                      <td><code class="fs-12">{{ tool.key }}</code></td>
                      <td class="text-muted fs-13 text-truncate" style="max-width: 380px;">{{ tool.description }}</td>
                      <td>{{ tool.agent_count }}</td>
                      <td>
                        <div class="form-check form-switch form-switch-md">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            :checked="tool.is_active"
                            @change="toggleToolActive(tool)"
                            style="cursor: pointer;"
                          />
                        </div>
                      </td>
                      <td>
                        <button class="btn btn-sm btn-soft-primary me-1" @click="openEditTool(tool)">
                          <i class="ri-pencil-line align-bottom"></i>
                        </button>
                        <button class="btn btn-sm btn-soft-danger" @click="deleteTool(tool)">
                          <i class="ri-delete-bin-line align-bottom"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tool Create/Edit Modal -->
      <div v-if="toolModalOpen" class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5); z-index: 1050;">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 shadow-lg">
            <div class="modal-header border-bottom py-3 px-4">
              <h5 class="modal-title fw-bold">{{ editingTool ? 'Edit Tool' : 'New Tool' }}</h5>
              <button type="button" class="btn-close" @click="closeToolModal"></button>
            </div>
            <div class="modal-body p-4">
              <div v-if="toolFormError" class="alert alert-danger py-2 fs-13">{{ toolFormError }}</div>

              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Name</label>
                  <input type="text" class="form-control" v-model="toolForm.name" placeholder="e.g. View Department Schedule" />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">
                    Key
                    <span class="text-muted fs-11">(matches a backend implementation)</span>
                  </label>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    v-model="toolForm.key"
                    placeholder="e.g. get_department_schedule"
                    :disabled="!!editingTool"
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">
                  Description
                  <span class="text-muted fs-11">(sent to the AI verbatim, guides when it decides to use this tool)</span>
                </label>
                <textarea class="form-control" rows="3" v-model="toolForm.description" placeholder="Explain to the AI what this tool does and when to use it..."></textarea>
              </div>

              <div class="mb-3">
                <label class="form-label fw-semibold">
                  Parameters Schema
                  <span class="text-muted fs-11">(JSON Schema, OpenAI function-calling format)</span>
                </label>
                <textarea class="form-control font-monospace fs-13" rows="6" v-model="toolForm.parameters_schema"></textarea>
              </div>

              <div class="form-check form-switch form-switch-md">
                <input class="form-check-input" type="checkbox" role="switch" v-model="toolForm.is_active" id="toolActiveSwitch" />
                <label class="form-check-label" for="toolActiveSwitch">Active</label>
              </div>
            </div>
            <div class="modal-footer border-top py-2 px-4">
              <button type="button" class="btn btn-soft-secondary btn-sm fw-semibold" @click="closeToolModal">Cancel</button>
              <button type="button" class="btn btn-primary btn-sm fw-semibold" @click="saveTool" :disabled="savingTool">
                <span v-if="savingTool" class="spinner-border spinner-border-sm me-1" role="status"></span>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </Layout>
</template>
